/**
 * RangeSlider Component
 *
 * Shared vanilla JS range filter for price and mileage SRP filters.
 */
(function (window) {
  'use strict';

  var DEFAULTS = {
    debounceMs: 450,
    step: 100,
    mobileBreakpoint: 768,
    handleSize: 44,
  };

  function RangeSlider(config) {
    if (!config.container) {
      console.error('RangeSlider: container element is required');
      return;
    }
    if (!config.dimension) {
      console.error('RangeSlider: dimension (price/mileage) is required');
      return;
    }

    this.config = Object.assign({}, DEFAULTS, config);
    this.dimension = config.dimension;
    this.container = config.container;
    this.vehicleCountLabel = config.vehicleCountLabel || 'vehicles';
    this.useMetric = !!config.useMetric;
    this.onSummaryChange = config.onSummaryChange || function () {};
    this.matchCount = config.initialMatchCount != null ? config.initialMatchCount : null;

    this.availableMin = config.min || 0;
    this.availableMax = config.max || 100000;

    var normalizedBounds = this.normalizeBounds(this.availableMin, this.availableMax);
    this.availableMin = normalizedBounds.min;
    this.availableMax = normalizedBounds.max;

    this.currentMin = config.currentMin != null ? config.currentMin : this.availableMin;
    this.currentMax = config.currentMax != null ? config.currentMax : this.availableMax;

    this.currentMin = this.clamp(this.currentMin, this.availableMin, this.availableMax);
    this.currentMax = this.clamp(this.currentMax, this.availableMin, this.availableMax);

    if (this.currentMin > this.currentMax) {
      var temp = this.currentMin;
      this.currentMin = this.currentMax;
      this.currentMax = temp;
    }

    this.onRangeChange = config.onRangeChange || function () {};
    this.formatValue = config.formatValue || this.defaultFormatValue.bind(this);
    this.parseValue = config.parseValue || this.defaultParseValue.bind(this);
    this.histogram = config.histogram || [];

    this.debounceTimer = null;
    this.isDragging = false;
    this.isMobile = window.innerWidth < this.config.mobileBreakpoint;
    this.focusedHandleIndex = 0;
    this.activeDragHandle = null;
    this._suppressRangeChange = false;
    this.elements = {};

    this.render();
    this.bindEvents();
    this.updateDisplay();
  }

  RangeSlider.prototype.clamp = function (value, min, max) {
    return Math.max(min, Math.min(max, value));
  };

  /**
   * noUiSlider requires max > min. When inventory collapses to one value, widen slightly.
   *
   * @return {{ min: number, max: number }}
   */
  RangeSlider.prototype.normalizeBounds = function (min, max) {
    min = parseInt(min, 10) || 0;
    max = parseInt(max, 10) || 0;

    if (min > max) {
      var temp = min;
      min = max;
      max = temp;
    }

    if (min === max) {
      var step = this.config.step || 1;
      if (min > 0) {
        min = Math.max(0, min - step);
      } else {
        max += step;
      }
    }

    return { min: min, max: max };
  };

  RangeSlider.prototype.syncSliderValues = function (min, max) {
    if (!this.elements.track || !this.elements.track.noUiSlider) {
      return;
    }

    this._suppressRangeChange = true;
    this.elements.track.noUiSlider.set([min, max]);
    this._suppressRangeChange = false;
  };

  RangeSlider.prototype.getDistanceUnit = function () {
    return this.useMetric ? 'km' : 'mi';
  };

  RangeSlider.prototype.isMileageDimension = function () {
    return this.dimension === 'mileage' || this.dimension === 'miles';
  };

  RangeSlider.prototype.formatPlainNumber = function (value) {
    // Year is a 4-digit identifier, not a quantity, so it renders without thousands separators.
    return this.dimension === 'year' ? String(value) : value.toLocaleString();
  };

  RangeSlider.prototype.formatCompactValue = function (value) {
    if (this.dimension === 'price') {
      return this.formatCompactPrice(value);
    }
    if (this.isMileageDimension()) {
      return this.formatCompactMileage(value);
    }
    return this.formatPlainNumber(value);
  };

  RangeSlider.prototype.defaultFormatValue = function (value) {
    if (this.dimension === 'price') {
      return '$' + value.toLocaleString();
    }
    if (this.dimension === 'mileage') {
      return value.toLocaleString() + ' ' + this.getDistanceUnit();
    }
    return this.formatPlainNumber(value);
  };

  RangeSlider.prototype.defaultParseValue = function (str) {
    var cleaned = String(str).replace(/[^0-9.-]/g, '');
    var parsed = parseInt(cleaned, 10);
    return isNaN(parsed) ? null : parsed;
  };

  RangeSlider.prototype.formatCompactPrice = function (value) {
    if (value >= 1000) {
      return '$' + Math.round(value / 1000) + 'k';
    }
    return '$' + value.toLocaleString();
  };

  RangeSlider.prototype.formatCompactMileage = function (value) {
    if (value >= 1000 && value % 1000 === 0) {
      return Math.round(value / 1000) + 'k ' + this.getDistanceUnit();
    }
    return value.toLocaleString() + ' ' + this.getDistanceUnit();
  };

  RangeSlider.prototype.formatCapLabel = function (value, edge) {
    if (this.dimension === 'price') {
      if (edge === 'max' && value >= 999000) {
        return '$999k';
      }
      return this.formatCompactPrice(value);
    }

    if (this.isMileageDimension()) {
      if (edge === 'max') {
        return Math.round(value / 1000) + 'k+';
      }
      return '0';
    }

    return edge === 'max' ? this.formatPlainNumber(value) + '+' : this.formatPlainNumber(value);
  };

  RangeSlider.prototype.isActive = function () {
    return this.currentMin !== this.availableMin || this.currentMax !== this.availableMax;
  };

  RangeSlider.prototype.isMinOpen = function () {
    return this.currentMin === this.availableMin;
  };

  RangeSlider.prototype.isMaxOpen = function () {
    return this.currentMax === this.availableMax;
  };

  RangeSlider.prototype.getLabel = function () {
    if (this.dimension === 'price') {
      return 'Price';
    }
    if (this.dimension === 'mileage') {
      return 'Mileage';
    }
    return this.dimension.charAt(0).toUpperCase() + this.dimension.slice(1);
  };

  RangeSlider.prototype.getAnyLabel = function () {
    if (this.dimension === 'price') {
      return 'Any Price';
    }
    if (this.dimension === 'mileage') {
      return 'Any Miles';
    }
    return 'Any ' + this.getLabel();
  };

  RangeSlider.prototype.formatRangeSummary = function () {
    if (!this.isActive()) {
      return this.getAnyLabel();
    }

    if (this.dimension === 'price') {
      if (this.isMinOpen() && !this.isMaxOpen()) {
        return 'Under ' + this.formatCompactPrice(this.currentMax);
      }
      if (!this.isMinOpen() && this.isMaxOpen()) {
        return this.formatCompactPrice(this.currentMin) + '+';
      }
      return (
        this.formatCompactPrice(this.currentMin) + ' — ' + this.formatCompactPrice(this.currentMax)
      );
    }

    if (this.isMileageDimension()) {
      if (this.isMinOpen() && !this.isMaxOpen()) {
        return 'Under ' + this.currentMax.toLocaleString() + ' ' + this.getDistanceUnit();
      }
      if (!this.isMinOpen() && this.isMaxOpen()) {
        return this.formatCompactMileage(this.currentMin) + '+';
      }
      return (
        this.formatCompactMileage(this.currentMin) +
        ' — ' +
        this.formatCompactMileage(this.currentMax)
      );
    }

    if (this.isMinOpen() && !this.isMaxOpen()) {
      return 'Under ' + this.formatPlainNumber(this.currentMax);
    }
    if (!this.isMinOpen() && this.isMaxOpen()) {
      return this.formatPlainNumber(this.currentMin) + '+';
    }
    return (
      this.formatPlainNumber(this.currentMin) + ' — ' + this.formatPlainNumber(this.currentMax)
    );
  };

  RangeSlider.prototype.getAccordionTitleRange = function () {
    if (!this.isActive()) {
      return '';
    }

    if (this.dimension === 'price') {
      if (this.isMinOpen() && !this.isMaxOpen()) {
        return 'Under ' + this.formatCompactPrice(this.currentMax);
      }
      if (!this.isMinOpen() && this.isMaxOpen()) {
        return this.formatCompactPrice(this.currentMin) + '+';
      }
      return (
        this.formatCompactPrice(this.currentMin) + ' - ' + this.formatCompactPrice(this.currentMax)
      );
    }

    if (this.isMileageDimension()) {
      if (this.isMinOpen() && !this.isMaxOpen()) {
        return 'Under ' + this.currentMax.toLocaleString() + ' ' + this.getDistanceUnit();
      }
      if (!this.isMinOpen() && this.isMaxOpen()) {
        return this.formatCompactMileage(this.currentMin) + '+';
      }
      return (
        this.formatCompactMileage(this.currentMin) +
        ' - ' +
        this.formatCompactMileage(this.currentMax)
      );
    }

    if (this.isMinOpen() && !this.isMaxOpen()) {
      return 'Under ' + this.formatPlainNumber(this.currentMax);
    }
    if (!this.isMinOpen() && this.isMaxOpen()) {
      return this.formatPlainNumber(this.currentMin) + '+';
    }
    return (
      this.formatPlainNumber(this.currentMin) + ' - ' + this.formatPlainNumber(this.currentMax)
    );
  };

  RangeSlider.prototype.getAccordionTitle = function () {
    var range = this.getAccordionTitleRange();
    return range ? '(' + range + ')' : '';
  };

  RangeSlider.prototype.render = function () {
    var dim = this.dimension;
    var isPrice = dim === 'price';
    var inputPrefix = isPrice ? '<span class="range-slider-input-prefix">$</span>' : '';
    var inputSuffix = this.isMileageDimension()
      ? '<span class="range-slider-input-suffix">' + this.getDistanceUnit() + '</span>'
      : '';

    this.container.innerHTML = '';
    this.container.className = 'range-slider-wrapper range-slider-' + dim;

    var html = [
      '<div class="range-slider-summary-block">',
      '  <div class="range-slider-summary-row">',
      '    <p class="range-slider-range-summary" id="' + dim + '-range-summary"></p>',
      '    <button type="button" class="range-slider-clear" aria-label="Clear ' +
        dim +
        ' filter">Clear</button>',
      '  </div>',
      '  <p class="range-slider-match-count" id="' + dim + '-match-count" aria-live="polite"></p>',
      '</div>',
      '<div class="range-slider-track-container">',
      '  <div class="range-slider-histogram" aria-hidden="true"></div>',
      '  <div class="range-slider-track-shell">',
      '    <div class="range-slider-track" id="' + dim + '-slider-track"></div>',
      '    <div class="range-slider-track-caps">',
      '      <span class="range-slider-track-cap range-slider-track-cap-min"></span>',
      '      <span class="range-slider-track-cap range-slider-track-cap-max"></span>',
      '    </div>',
      '  </div>',
      '</div>',
      '<div class="range-slider-inputs">',
      '  <div class="range-slider-input-group">',
      '    <label class="range-slider-field-label" for="' + dim + '-min-input">Min</label>',
      '    <div class="range-slider-input-shell">',
      inputPrefix,
      '      <input type="text" id="' +
        dim +
        '-min-input" class="range-slider-input range-slider-min"',
      '             inputmode="numeric" pattern="[0-9]*" placeholder="No min"',
      '             aria-describedby="' + dim + '-range-summary">',
      inputSuffix,
      '    </div>',
      '  </div>',
      '  <span class="range-slider-separator" aria-hidden="true">—</span>',
      '  <div class="range-slider-input-group">',
      '    <label class="range-slider-field-label" for="' + dim + '-max-input">Max</label>',
      '    <div class="range-slider-input-shell">',
      inputPrefix,
      '      <input type="text" id="' +
        dim +
        '-max-input" class="range-slider-input range-slider-max"',
      '             inputmode="numeric" pattern="[0-9]*" placeholder="No max"',
      '             aria-describedby="' + dim + '-range-summary">',
      inputSuffix,
      '    </div>',
      '  </div>',
      '</div>',
      '<div class="range-slider-live-region visually-hidden" aria-live="polite" aria-atomic="true"></div>',
    ].join('\n');

    this.container.innerHTML = html;

    this.elements.summary = this.container.querySelector('.range-slider-range-summary');
    this.elements.matchCount = this.container.querySelector('.range-slider-match-count');
    this.elements.minInput = this.container.querySelector('.range-slider-min');
    this.elements.maxInput = this.container.querySelector('.range-slider-max');
    this.elements.track = this.container.querySelector('.range-slider-track');
    this.elements.histogram = this.container.querySelector('.range-slider-histogram');
    this.elements.clearBtn = this.container.querySelector('.range-slider-clear');
    this.elements.liveRegion = this.container.querySelector('.range-slider-live-region');
    this.elements.capMin = this.container.querySelector('.range-slider-track-cap-min');
    this.elements.capMax = this.container.querySelector('.range-slider-track-cap-max');

    this.initSlider();

    if (this.histogram.length > 0) {
      this.renderHistogram();
    }
  };

  RangeSlider.prototype.formatInputValue = function (value) {
    return this.formatPlainNumber(value);
  };

  RangeSlider.prototype.initSlider = function () {
    var self = this;
    var track = this.elements.track;

    if (typeof noUiSlider === 'undefined') {
      console.error('RangeSlider: noUiSlider library is required');
      return;
    }

    noUiSlider.create(track, {
      start: [this.currentMin, this.currentMax],
      connect: true,
      range: {
        min: this.availableMin,
        max: this.availableMax,
      },
      step: this.config.step,
      behaviour: 'tap-drag',
      tooltips: false,
      handleAttributes: [
        { 'aria-label': 'Minimum ' + this.dimension },
        { 'aria-label': 'Maximum ' + this.dimension },
      ],
    });

    this.decorateHandles();
    this.setupHandleAccessibility();

    track.noUiSlider.on('start', function (values, handleNumber) {
      self.isDragging = true;
      self.activeDragHandle =
        typeof handleNumber === 'number' ? handleNumber : self.focusedHandleIndex;
      self.updateHandleTooltips(
        Math.round(values[0]),
        Math.round(values[1]),
        true,
        self.activeDragHandle,
      );
    });

    track.noUiSlider.on('slide', function (values, handleNumber) {
      self.isDragging = true;
      var min = Math.round(values[0]);
      var max = Math.round(values[1]);

      if (typeof handleNumber === 'number') {
        self.activeDragHandle = handleNumber;
      }

      self.currentMin = min;
      self.currentMax = max;
      self.updateInputFields(min, max);
      self.updateSummaryDisplay();
      self.updateHandleTooltips(min, max, true, self.activeDragHandle);
      self.renderHistogram();
      self.updateHandleAria(min, max);
    });

    track.noUiSlider.on('change', function (values) {
      if (self._suppressRangeChange) {
        return;
      }

      self.isDragging = false;
      self.activeDragHandle = null;
      var min = Math.round(values[0]);
      var max = Math.round(values[1]);
      self.setRange(min, max, true);
      self.updateHandleTooltips(min, max, false);
    });

    track.noUiSlider.on('end', function () {
      self.isDragging = false;
      self.activeDragHandle = null;
      self.updateHandleTooltips(self.currentMin, self.currentMax, false);
    });
  };

  RangeSlider.prototype.decorateHandles = function () {
    var handles = this.elements.track.querySelectorAll('.noUi-handle');
    for (var i = 0; i < handles.length; i++) {
      if (!handles[i].querySelector('.range-slider-handle-grip')) {
        var grip = document.createElement('span');
        grip.className = 'range-slider-handle-grip';
        grip.setAttribute('aria-hidden', 'true');
        grip.innerHTML = '<span></span><span></span><span></span>';
        handles[i].appendChild(grip);

        var tooltip = document.createElement('span');
        tooltip.className = 'range-slider-handle-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        handles[i].appendChild(tooltip);
      }
    }
  };

  RangeSlider.prototype.setupHandleAccessibility = function () {
    var self = this;
    var track = this.elements.track;
    var handles = track.querySelectorAll('.noUi-handle');

    track.removeAttribute('role');
    track.removeAttribute('tabindex');
    track.removeAttribute('aria-valuemin');
    track.removeAttribute('aria-valuemax');
    track.removeAttribute('aria-valuenow');
    track.removeAttribute('aria-valuetext');

    for (var i = 0; i < handles.length; i++) {
      handles[i].setAttribute('role', 'slider');
      handles[i].setAttribute('tabindex', i === 0 ? '0' : '-1');
      handles[i].setAttribute('data-handle-index', String(i));

      handles[i].addEventListener('focus', function () {
        self.focusedHandleIndex = parseInt(this.getAttribute('data-handle-index'), 10) || 0;
        self.updateHandleTabOrder();
      });

      handles[i].addEventListener('keydown', function (e) {
        var index = parseInt(this.getAttribute('data-handle-index'), 10) || 0;
        self.handleHandleKeydown(e, index);
      });
    }

    this.updateHandleAria(this.currentMin, this.currentMax);
  };

  RangeSlider.prototype.updateHandleTabOrder = function () {
    var handles = this.elements.track.querySelectorAll('.noUi-handle');
    for (var i = 0; i < handles.length; i++) {
      handles[i].setAttribute('tabindex', i === this.focusedHandleIndex ? '0' : '-1');
    }
  };

  RangeSlider.prototype.getHandles = function () {
    return this.elements.track ? this.elements.track.querySelectorAll('.noUi-handle') : [];
  };

  RangeSlider.prototype.updateHandleAria = function (min, max) {
    var handles = this.getHandles();
    if (!handles.length) {
      return;
    }

    handles[0].setAttribute('aria-valuemin', String(this.availableMin));
    handles[0].setAttribute('aria-valuemax', String(max));
    handles[0].setAttribute('aria-valuenow', String(min));
    handles[0].setAttribute('aria-valuetext', this.formatValue(min));

    handles[1].setAttribute('aria-valuemin', String(min));
    handles[1].setAttribute('aria-valuemax', String(this.availableMax));
    handles[1].setAttribute('aria-valuenow', String(max));
    handles[1].setAttribute('aria-valuetext', this.formatValue(max));
  };

  RangeSlider.prototype.updateHandleTooltips = function (min, max, visible, activeHandleIndex) {
    var handles = this.getHandles();
    if (!handles.length) {
      return;
    }

    var minLabel = this.formatCompactValue(min);
    var maxLabel = this.formatCompactValue(max);
    var labels = [minLabel, maxLabel];

    for (var i = 0; i < handles.length; i++) {
      var tooltip = handles[i].querySelector('.range-slider-handle-tooltip');
      if (tooltip) {
        tooltip.textContent = labels[i];
      }

      var showTooltip = !!visible && (activeHandleIndex == null || activeHandleIndex === i);
      handles[i].classList.toggle('show-tooltip', showTooltip);
    }
  };

  RangeSlider.prototype.handleHandleKeydown = function (e, handleIndex) {
    var step = this.config.step;
    var largeStep = step * 10;
    var delta = e.shiftKey ? largeStep : step;
    var handled = true;
    var min = this.currentMin;
    var max = this.currentMax;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        if (handleIndex === 0) {
          min -= delta;
        } else {
          max -= delta;
        }
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        if (handleIndex === 0) {
          min += delta;
        } else {
          max += delta;
        }
        break;
      case 'Home':
        if (handleIndex === 0) {
          min = this.availableMin;
        } else {
          max = this.availableMin;
        }
        break;
      case 'End':
        if (handleIndex === 0) {
          min = this.availableMax;
        } else {
          max = this.availableMax;
        }
        break;
      case 'Tab':
        handled = false;
        break;
      default:
        handled = false;
    }

    if (handled) {
      e.preventDefault();
      this.focusedHandleIndex = handleIndex;
      this.setRange(min, max, true);
    }
  };

  RangeSlider.prototype.bindEvents = function () {
    var self = this;

    this.elements.minInput.addEventListener('blur', function () {
      self.handleInputChange('min');
    });
    this.elements.minInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        self.handleInputChange('min');
        this.blur();
      }
    });

    this.elements.maxInput.addEventListener('blur', function () {
      self.handleInputChange('max');
    });
    this.elements.maxInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        self.handleInputChange('max');
        this.blur();
      }
    });

    if (this.isMobile && this.config.mobileModalEnabled !== false) {
      this.elements.minInput.addEventListener('focus', function (e) {
        e.preventDefault();
        self.openMobileModal('min');
      });
      this.elements.maxInput.addEventListener('focus', function (e) {
        e.preventDefault();
        self.openMobileModal('max');
      });
    }

    this.elements.clearBtn.addEventListener('click', function (e) {
      e.preventDefault();
      self.clear();
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var wasMobile = self.isMobile;
        self.isMobile = window.innerWidth < self.config.mobileBreakpoint;
        if (wasMobile !== self.isMobile) {
          self.render();
          self.bindEvents();
          self.updateDisplay();
        }
      }, 250);
    });
  };

  RangeSlider.prototype.handleInputChange = function (which) {
    var input = which === 'min' ? this.elements.minInput : this.elements.maxInput;
    var raw = input.value.trim();

    if (!raw) {
      if (which === 'min') {
        this.setRange(this.availableMin, this.currentMax, true);
      } else {
        this.setRange(this.currentMin, this.availableMax, true);
      }
      return;
    }

    var parsed = this.parseValue(raw);
    if (parsed === null) {
      this.updateInputFields(this.currentMin, this.currentMax);
      return;
    }

    parsed = this.clamp(parsed, this.availableMin, this.availableMax);

    if (which === 'min') {
      this.setRange(parsed, this.currentMax, true);
    } else {
      this.setRange(this.currentMin, parsed, true);
    }
  };

  RangeSlider.prototype.updateInputFields = function (min, max) {
    var minInput = this.elements.minInput;
    var maxInput = this.elements.maxInput;

    if (!this.isActive() || this.isMinOpen()) {
      minInput.value = '';
    } else {
      minInput.value = this.formatInputValue(min);
    }

    if (!this.isActive() || this.isMaxOpen()) {
      maxInput.value = '';
    } else {
      maxInput.value = this.formatInputValue(max);
    }
  };

  RangeSlider.prototype.updateSummaryDisplay = function () {
    if (this.elements.summary) {
      this.elements.summary.textContent = this.formatRangeSummary();
    }

    if (this.elements.capMin) {
      this.elements.capMin.textContent = this.formatCapLabel(this.availableMin, 'min');
    }
    if (this.elements.capMax) {
      this.elements.capMax.textContent = this.formatCapLabel(this.availableMax, 'max');
    }

    this.updateMatchCountDisplay();
    this.onSummaryChange(this.formatRangeSummary(), this.getAccordionTitle());
  };

  RangeSlider.prototype.updateMatchCountDisplay = function () {
    if (!this.elements.matchCount) {
      return;
    }

    if (this.matchCount == null) {
      this.elements.matchCount.textContent = '';
      return;
    }

    this.elements.matchCount.textContent =
      this.matchCount.toLocaleString() + ' ' + this.vehicleCountLabel;
  };

  RangeSlider.prototype.updateMatchCount = function (count) {
    this.matchCount = count;
    this.updateMatchCountDisplay();
  };

  RangeSlider.prototype.setRange = function (min, max, triggerCallback) {
    min = this.clamp(min, this.availableMin, this.availableMax);
    max = this.clamp(max, this.availableMin, this.availableMax);

    if (min > max) {
      var temp = min;
      min = max;
      max = temp;
    }

    this.currentMin = min;
    this.currentMax = max;

    this.updateDisplay();

    if (this.elements.track && this.elements.track.noUiSlider) {
      this.syncSliderValues(min, max);
    }

    if (triggerCallback) {
      this.debouncedCallback();
    }
  };

  RangeSlider.prototype.updateDisplay = function () {
    this.updateInputFields(this.currentMin, this.currentMax);
    this.updateSummaryDisplay();
    this.updateHandleAria(this.currentMin, this.currentMax);
    this.renderHistogram();
    this.announceChange();
  };

  RangeSlider.prototype.announceChange = function () {
    if (!this.elements.liveRegion) {
      return;
    }
    this.elements.liveRegion.textContent = this.getLabel() + ' filter ' + this.formatRangeSummary();
  };

  RangeSlider.prototype.debouncedCallback = function () {
    var self = this;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(function () {
      self.onRangeChange(self.currentMin, self.currentMax);
    }, this.config.debounceMs);
  };

  RangeSlider.prototype.clear = function () {
    this.setRange(this.availableMin, this.availableMax, true);
  };

  RangeSlider.prototype.updateBounds = function (min, max) {
    // When handles sit at the previous full range ("Any Price/Miles"), expand or
    // shrink to the new inventory bounds instead of treating old endpoints as filters.
    var wasAtFullRange = !this.isActive();
    var normalized = this.normalizeBounds(min, max);

    min = normalized.min;
    max = normalized.max;

    this.availableMin = min;
    this.availableMax = max;

    if (wasAtFullRange) {
      this.currentMin = min;
      this.currentMax = max;
    } else {
      this.currentMin = this.clamp(this.currentMin, min, max);
      this.currentMax = this.clamp(this.currentMax, min, max);
    }

    if (this.elements.track && this.elements.track.noUiSlider) {
      this.elements.track.noUiSlider.updateOptions({
        range: {
          min: min,
          max: max,
        },
      });
      this.syncSliderValues(this.currentMin, this.currentMax);
    }

    this.decorateHandles();
    this.setupHandleAccessibility();
    this.updateDisplay();
  };

  RangeSlider.prototype.updateHistogram = function (histogram) {
    this.histogram = histogram || [];
    this.renderHistogram();
  };

  RangeSlider.prototype.renderHistogram = function () {
    var container = this.elements.histogram;
    if (!container || this.histogram.length === 0) {
      if (container) {
        container.innerHTML = '';
      }
      return;
    }

    var maxCount = 0;
    for (var i = 0; i < this.histogram.length; i++) {
      if (this.histogram[i].count > maxCount) {
        maxCount = this.histogram[i].count;
      }
    }

    if (maxCount === 0) {
      container.innerHTML = '';
      return;
    }

    var html = '<div class="range-slider-histogram-bars">';
    for (var j = 0; j < this.histogram.length; j++) {
      var bucket = this.histogram[j];
      var heightPercent = (bucket.count / maxCount) * 100;
      var overlaps =
        bucket.max > this.currentMin &&
        bucket.min < this.currentMax &&
        (this.isActive() || bucket.min >= this.availableMin);

      html +=
        '<div class="range-slider-histogram-bar' +
        (overlaps && this.isActive() ? ' active' : '') +
        '"';
      html += ' style="height: ' + heightPercent + '%"';
      html +=
        ' title="' +
        this.formatValue(bucket.min) +
        ' - ' +
        this.formatValue(bucket.max) +
        ': ' +
        bucket.count +
        ' vehicles"';
      html += '></div>';
    }
    html += '</div>';

    container.innerHTML = html;

    var bars = container.querySelectorAll('.range-slider-histogram-bar');
    for (var k = 0; k < bars.length; k++) {
      bars[k].style.setProperty('--bar-index', String(k));
      bars[k].classList.add('range-slider-histogram-bar--enter');
    }
  };

  RangeSlider.prototype.openMobileModal = function (which) {
    var self = this;
    var currentValue = which === 'min' ? this.currentMin : this.currentMax;
    var label = which === 'min' ? 'Minimum' : 'Maximum';
    var hasOpenValue =
      which === 'min' ? !this.isActive() || this.isMinOpen() : !this.isActive() || this.isMaxOpen();

    var overlay = document.createElement('div');
    overlay.className = 'range-slider-modal-overlay range-slider-modal-overlay--preparing';
    var previousBodyOverflow = document.body.style.overflow;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', label + ' ' + this.getLabel() + ' input');

    var modal = document.createElement('div');
    modal.className = 'range-slider-modal';
    modal.innerHTML = [
      '<div class="range-slider-modal-header">',
      '  <span>' + label + ' ' + this.getLabel() + '</span>',
      '  <button type="button" class="range-slider-modal-close" aria-label="Cancel">&times;</button>',
      '</div>',
      '<div class="range-slider-modal-body">',
      '  <input type="text" class="range-slider-modal-input" inputmode="numeric" pattern="[0-9]*"',
      '         value="' + (hasOpenValue ? '' : this.formatInputValue(currentValue)) + '"',
      '         placeholder="' + (which === 'min' ? 'No min' : 'No max') + '"',
      '         aria-label="' + label + ' value">',
      '</div>',
      '<div class="range-slider-modal-footer">',
      '  <button type="button" class="range-slider-modal-cancel">Cancel</button>',
      '  <button type="button" class="range-slider-modal-confirm">Confirm</button>',
      '</div>',
    ].join('\n');

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    document.body.classList.add('range-slider-modal-open');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      overlay.classList.remove('range-slider-modal-overlay--preparing');
    });

    var input = modal.querySelector('.range-slider-modal-input');
    setTimeout(function () {
      input.focus();
      input.select();
    }, 100);

    var closeModal = function () {
      document.body.classList.remove('range-slider-modal-open');
      document.body.style.overflow = previousBodyOverflow;
      overlay.classList.add('closing');
      setTimeout(function () {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 200);
    };

    overlay.querySelector('.range-slider-modal-cancel').addEventListener('click', closeModal);
    overlay.querySelector('.range-slider-modal-close').addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closeModal();
      }
    });

    var confirmValue = function () {
      var parsed = input.value.trim() ? self.parseValue(input.value) : null;
      if (parsed === null) {
        if (which === 'min') {
          self.setRange(self.availableMin, self.currentMax, true);
        } else {
          self.setRange(self.currentMin, self.availableMax, true);
        }
      } else {
        parsed = self.clamp(parsed, self.availableMin, self.availableMax);
        if (which === 'min') {
          self.setRange(parsed, self.currentMax, true);
        } else {
          self.setRange(self.currentMin, parsed, true);
        }
      }
      closeModal();
    };

    overlay.querySelector('.range-slider-modal-confirm').addEventListener('click', confirmValue);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmValue();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      }
    });

    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') {
        var focusables = modal.querySelectorAll('input, button');
        var first = focusables[0];
        var last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  };

  RangeSlider.prototype.getRange = function () {
    return {
      min: this.currentMin,
      max: this.currentMax,
    };
  };

  RangeSlider.prototype.getUrlParamNames = function () {
    if (this.dimension === 'price') {
      return { min: 'min_price', max: 'max_price' };
    }
    if (this.dimension === 'mileage') {
      return { min: 'min_miles', max: 'max_miles' };
    }
    return { min: 'min_' + this.dimension, max: 'max_' + this.dimension };
  };

  RangeSlider.prototype.destroy = function () {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.elements.track && this.elements.track.noUiSlider) {
      this.elements.track.noUiSlider.destroy();
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
  };

  window.RangeSlider = RangeSlider;
})(window);
